// internal-deps
import {
    DineoutCartType,
    GlobalHeaderState,
    HTTP_METHODS,
    makeDownstreamApiCallDeprecated,
    SwgyHeaderKeysList,
} from "@swiggy-private/checkout-core";
import { ApiResponse, TEXTS, unwrapResponseWithCode } from "@swiggy-private/core-utils";
import { Err, Ok, Result } from "@swiggy-private/js-fp";

// interfaces
import { IDineoutCartResponse } from "@dineout-cart-client/interfaces";
import { ILocalCart } from "@dineout-cart-client/interfaces/LocalCart";

// constants
import { DOWNSTREAM_ENDPOINTS, DOWNSTREAM_HOSTS } from "@dineout-cart-client/constants/Hosts";

// utils
import { format, isEmpty, isEqual } from "@swiggy-private/js-utils";
import {
    CART_META_DATA_TYPE,
    CART_TYPE,
    CLIENT_ID,
    SERVICE_LINE,
    SOURCE,
    USE_V2_LOCAL_CART_FORMAT_STORAGE_KEY,
} from "@dineout-cart-client/constants";
import { isStealStoreCart, isTableReservationCart } from "@dineout-cart-client/helpers/cartHelper";
import { CART_STATUS_CODES } from "@dineout-cart-client/constants/Error";
import { isWebPlatform, Storage } from "@swiggy-private/core-ui";

async function updateDineoutCart(
    cartType: DineoutCartType | null,
    localCart: ILocalCart,
): Promise<IDineoutCartResponse> {
    let finalLocalCart: ILocalCart;

    const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";

    // TODO: Remove this flag once seo is completely rolled out, this is to skip transformation only during the transition period until seo flow is completely migrated to updated transformation for local cart
    const useV2LocalCartFormat = Storage.getItem(USE_V2_LOCAL_CART_FORMAT_STORAGE_KEY) === "true";

    // Some changes are required for table reservation cart to ensure backward compatibility with the web flow
    // TODO: to remove this check altogether and have consistent handling for all cart types
    if (!useV2LocalCartFormat && isTableReservationCart(cartType) && isWebPlatform(platform)) {
        const { restaurantId, itemId, count, dealCategory, selectedSlot } = localCart;

        finalLocalCart = {
            cartMetaData: {
                couponCode: null,
                dealCategory: dealCategory,
                requestTimestamp: Date.now() / 1000,
                restaurantId: restaurantId || "",
                type: CART_META_DATA_TYPE,
            },
            cartType: CART_TYPE,
            items: [
                {
                    itemId: itemId,
                    meta: { slot: selectedSlot },
                    quantity: count,
                    serviceLine: SERVICE_LINE,
                },
            ],
            location: {
                latitude: GlobalHeaderState.getHeader(SwgyHeaderKeysList.lat),
                longitude: GlobalHeaderState.getHeader(SwgyHeaderKeysList.lng),
            },
            source: SOURCE,
        };
    } else {
        finalLocalCart = localCart;
    }

    if (!isEmpty(finalLocalCart.cartMetaData)) {
        finalLocalCart.cartMetaData.clientId = CLIENT_ID;
    }

    const response = (await makeDownstreamApiCallDeprecated({
        apiHost: DOWNSTREAM_HOSTS.PAYMENTS_HOST,
        endpoint: DOWNSTREAM_ENDPOINTS.UPDATE_CART_ENDPOINT,
        options: {
            method: HTTP_METHODS.POST,
            body: JSON.stringify(finalLocalCart),
        },
    })) as IDineoutCartResponse;

    return response;
}

const isCustomErrorHandled = (cartType: DineoutCartType | null, cartResponse?: IDineoutCartResponse): boolean => {
    switch (cartType) {
        case DineoutCartType.STEAL_STORE_CART:
            return true;
        case DineoutCartType.TABLE_RESERVATION_CART:
            return isEqual(cartResponse?.statusCode, CART_STATUS_CODES.TABLE_RESERVATION_CHECKED_IN);
        default:
            return false;
    }
};

export function updateDineoutCartWrapper(
    cartType: DineoutCartType | null,
    localCart: ILocalCart,
): Promise<Result<IDineoutCartResponse, Error | ApiResponse>> {
    return updateDineoutCart(cartType, localCart)
        .then((cartResponse) => {
            // This is required because we need to store complete response in the cart context to handle steal deal cases
            // for which we need to remove the root level api check for unwrapResponseWithStatusCode
            // TODO: remove this post proper testing and handling is done for non-zero error cases
            if (cartResponse?.statusCode !== 0 && !isCustomErrorHandled(cartType, cartResponse)) {
                throw {
                    ...cartResponse,
                    statusMessage: cartResponse?.statusMessage ?? TEXTS.OOPS_SOMETHING_WENT_WRONG,
                };
            }

            if (isEmpty(cartResponse?.data) && isStealStoreCart(cartType)) {
                throw {
                    ...cartResponse,
                    statusMessage: cartResponse?.statusMessage ?? TEXTS.OOPS_SOMETHING_WENT_WRONG,
                };
            }

            return Ok(cartResponse);
        })
        .catch((err) => {
            return Err(err);
        });
}
async function validateDineoutCart(cartType: string): Promise<IDineoutCartResponse["data"]> {
    const endpoint = format(DOWNSTREAM_ENDPOINTS.VALIDATE_CART_ENDPOINT, cartType);
    const response = unwrapResponseWithCode<IDineoutCartResponse["data"]>(
        (await makeDownstreamApiCallDeprecated({
            apiHost: DOWNSTREAM_HOSTS.PAYMENTS_HOST,
            endpoint: endpoint,
            options: {
                method: HTTP_METHODS.GET,
            },
        })) as IDineoutCartResponse,
    );

    return response.toPromise();
}
export function validateDineoutCartWrapper(
    cartType: string,
): Promise<Result<{ isValidCart: boolean }, Error | ApiResponse>> {
    return validateDineoutCart(cartType)
        .then(() => {
            return Ok({ isValidCart: true });
        })
        .catch((err) => {
            return Err(err);
        });
}
